import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useOnboardingStore } from '@/stores/onboarding'
import { cancelAllRequests } from '@/api/index'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/RegisterView.vue'),
    meta: { requiresAuth: false, requiresGuest: true }
  },
  {
    path: '/auth/email-confirm/:token',
    name: 'email-confirm',
    component: () => import('@/views/auth/EmailConfirmView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/set-password',
    name: 'set-password',
    component: () => import('@/views/auth/SetPasswordView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/password-reset/:token',
    name: 'password-reset',
    component: () => import('@/views/auth/ResetPasswordView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/pricing',
    name: 'pricing',
    component: () => import('@/views/PricingView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/billing/success',
    name: 'billing-success',
    component: () => import('@/views/BillingSuccessView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/billing/fail',
    name: 'billing-fail',
    component: () => import('@/views/BillingFailView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/views/legal/TermsView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('@/views/legal/PrivacyView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/user-agreement',
    name: 'user-agreement',
    component: () => import('@/views/legal/UserAgreementView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/pd-consent',
    name: 'pd-consent',
    component: () => import('@/views/legal/PersonalDataConsentView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/cookies',
    name: 'cookies',
    component: () => import('@/views/legal/CookiesPolicyView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/',
    name: 'landing',
    component: () => import('@/views/LandingView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/onboarding',
    redirect: '/app/onboarding',
    meta: { requiresAuth: true }
  },
  {
    path: '/help/faq',
    redirect: '/app/help/faq',
    meta: { requiresAuth: true }
  },
  {
    path: '/products',
    redirect: '/app/products',
    meta: { requiresAuth: true }
  },
  {
    path: '/planning',
    redirect: '/app/planning',
    meta: { requiresAuth: true }
  },
  {
    path: '/planning-page',
    redirect: '/app/planning-page',
    meta: { requiresAuth: true }
  },
  {
    path: '/inventory',
    redirect: '/app/inventory',
    meta: { requiresAuth: true }
  },
  {
    path: '/reports',
    redirect: '/app/reports',
    meta: { requiresAuth: true }
  },
  {
    path: '/settings',
    redirect: '/app/settings',
    meta: { requiresAuth: true }
  },
  {
    path: '/settings/:pathMatch(.*)*',
    redirect: (to) => {
      const rest = Array.isArray(to.params.pathMatch) ? to.params.pathMatch.join('/') : String(to.params.pathMatch ?? '')
      return rest ? `/app/settings/${rest}` : '/app/settings'
    },
    meta: { requiresAuth: true }
  },
  {
    path: '/app',
    component: () => import('@/views/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue')
      },
      {
        path: 'planning',
        name: 'planning',
        component: () => import('@/views/planning/PlanningView.vue')
      },
      {
        path: 'planning-page',
        name: 'planning-page',
        component: () => import('@/views/planning/PlanningPage.vue')
      },
      {
        path: 'inventory',
        name: 'inventory',
        component: () => import('@/views/inventory/InventoryView.vue')
      },
      {
        path: 'products',
        name: 'products',
        component: () => import('@/views/products/ProductsView.vue'),
        meta: { roles: ['superadmin', 'company_admin', 'sales_director', 'marketplace_manager'] }
      },
      {
        path: 'reports',
        name: 'reports',
        component: () => import('@/views/reports/ReportsView.vue')
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/settings/SettingsView.vue'),
        children: []
      },
      {
        path: 'settings/profile',
        name: 'settings-profile',
        component: () => import('@/views/settings/ProfileView.vue'),
      },
      {
        path: 'settings/companies',
        name: 'settings-companies',
        component: () => import('@/views/settings/CompaniesView.vue'),
        meta: { roles: ['superadmin'] }
      },
      {
        path: 'settings/users',
        name: 'settings-users',
        component: () => import('@/views/settings/UsersView.vue'),
        meta: { roles: ['superadmin', 'company_admin'] }
      },
      {
        path: 'settings/legal-entities',
        name: 'settings-legal-entities',
        component: () => import('@/views/settings/LegalEntitiesView.vue'),
        meta: { roles: ['superadmin', 'company_admin'] }
      },
      {
        path: 'settings/cabinets',
        name: 'settings-cabinets',
        component: () => import('@/views/settings/CabinetsView.vue'),
        meta: { roles: ['superadmin', 'company_admin', 'sales_director'] }
      },
      {
        path: 'settings/categories',
        name: 'settings-categories',
        component: () => import('@/views/settings/CategoriesView.vue'),
        meta: { roles: ['superadmin', 'company_admin', 'sales_director'] }
      },
      {
        path: 'settings/suppliers',
        name: 'settings-suppliers',
        component: () => import('@/views/settings/SuppliersView.vue'),
        meta: { roles: ['superadmin', 'company_admin', 'sales_director'] }
      },
      {
        path: 'settings/product-groups',
        name: 'settings-product-groups',
        component: () => import('@/views/settings/ProductGroupsView.vue'),
        meta: { roles: ['superadmin', 'company_admin', 'sales_director'] }
      },
      {
        path: 'settings/billing',
        name: 'settings-billing',
        component: () => import('@/views/settings/BillingView.vue'),
        meta: { roles: ['superadmin', 'company_admin'] }
      },
      {
        path: 'onboarding',
        name: 'onboarding',
        component: () => import('@/views/OnboardingView.vue')
      },
      {
        path: 'help/faq',
        name: 'help-faq',
        component: () => import('@/views/help/FaqView.vue')
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue')
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

// Navigation guard for authentication and request cancellation
router.beforeEach(async (to, _from, next) => {
  // Cancel all pending requests on navigation
  cancelAllRequests()

  const authStore = useAuthStore()

  // Check if route requires authentication
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth !== false)

  if (requiresAuth && !authStore.isAuthenticated) {
    // Try to restore session from localStorage
    await authStore.checkAuth()

    if (!authStore.isAuthenticated) {
      next({ name: 'login', query: { redirect: to.fullPath } })
      return
    }
  }

  // Redirect to dashboard if already authenticated and trying to access login or guest-only routes
  if (to.name === 'login' && authStore.isAuthenticated) {
    next({ name: 'dashboard' })
    return
  }

  // requiresGuest: redirect authenticated users away from register page
  const requiresGuest = to.matched.some(record => record.meta.requiresGuest === true)
  if (requiresGuest && authStore.isAuthenticated) {
    next({ name: 'dashboard' })
    return
  }

  if (requiresAuth && authStore.isAuthenticated && authStore.user?.id) {
    const onboardingStore = useOnboardingStore()
    await onboardingStore.initForUser(authStore.user.id)

    try {
      await onboardingStore.ensureFresh(60_000)
    } catch (e) {
      void e
    }

    if (onboardingStore.shouldRedirectToOnboarding(to.path)) {
      next({ name: 'onboarding' })
      return
    }
  }

  // RBAC Guard
  const requiredRoles = to.meta.roles as string[] | undefined
  if (requiredRoles && authStore.userRole) {
    if (!requiredRoles.includes(authStore.userRole)) {
      // User does not have permission
      // Redirect to 403 or dashboard? Dashboard is safer.
      next({ name: 'dashboard' }) 
      return
    }
  }

  next()
})

export default router
